import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, TreeSelect, Select } from 'antd';
import { ReactSortable } from 'react-sortablejs';
import PropTypes from 'prop-types';

import AddImageIcon from 'components/Icons/AddImageIcon';
import EditableSelect from 'components/EditableSelect/EditableSelect';
import CloseIcon from 'components/Icons/CloseIcon';
import DragIcon from 'components/Icons/DragIcon';
import FolderIcon from 'components/Icons/FolderIcon';
import AddFolder from 'components/Icons/AddFolder';
import DropdownIcon from 'components/Icons/DropdownIcon';

import './CatalogAddProduct.scss';

const { TextArea } = Input;

/**
 * Catalog add product
 *
 * @param {Object} props
 * @param {Object} props.addProductForm
 * @param {Object} props.product
 * @param {Object} props.treeDataFromProps
 * @param {Function} props.onUploadClick
 * @param {Array} props.catalogThumbnails
 * @param {string} props.selectedFolderKey
 * @param {Object} props.user
 * @param {Function} props.setCatalogThumbnails
 */
const CatalogAddProduct = ({
  addProductForm,
  product,
  onUploadClick,
  catalogThumbnails,
  user,
  setCatalogThumbnails,
  treeData,
  handleTreeSearch,
  addNewChildFromSearch,
  selectedFolderKey,
  onSelectTreeItem,
  setTreeSearch,
}) => {
  const [discountType, setDiscountType] = useState(
    product?.discount?.unit || user.profile.currency || '$'
  );
  const [paymentType, setPaymentType] = useState(product?.price?.type || '');
  const currency = user.profile.currency || '$';

  /**
   * Get title from treeData based on folderKey
   * @param {string} folderKey - The folder key to search for
   * @param {Array} children - The tree children array
   * @returns {string} - The title of the folder
   */
  const getFolderKeyFromTreeData = (folderKey, children = []) => {
    for (const child of children) {
      if (child.path === folderKey) {
        return { key: child.key, path: child.path };
      }
      if (child.children && child.children.length > 0) {
        const foundKey = getFolderKeyFromTreeData(folderKey, child.children);
        if (foundKey) {
          return foundKey;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    setCatalogThumbnails([...(product?.images || []), ...catalogThumbnails] || []);
    const defaultPath = getFolderKeyFromTreeData(
      product?.folderKey || selectedFolderKey,
      treeData?.children || []
    )?.path;
    onSelectTreeItem(defaultPath ? [[defaultPath]] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="catalog-add-product">
      <div className="catalog-add-product__header">
        <div className="catalog-add-product__title">{product ? 'Edit' : 'New'} product</div>
        <div className="catalog-add-product__description">*All values can be changed later</div>
      </div>

      <div className="catalog-add-product__card">
        <div className="catalog-add-product__image-section">
          <ReactSortable
            list={catalogThumbnails}
            setList={(newState) => {
              if (JSON.stringify(catalogThumbnails) !== JSON.stringify(newState)) {
                setCatalogThumbnails(newState);
              }
            }}
            animation={400}
            className="sortable-container"
            handle=".handle">
            {catalogThumbnails.map((thumbnail) => (
              <div key={thumbnail.url} className="catalog-add-product__image-container">
                <div className="handle">
                  <DragIcon />
                  <DragIcon />
                </div>
                <CloseIcon
                  className="catalog-add-product__close-icon"
                  onClick={() => {
                    const tempCatalogThumbnails = catalogThumbnails.filter(
                      (image) => image.url !== thumbnail.url
                    );
                    setCatalogThumbnails(tempCatalogThumbnails);
                  }}
                />
                <img
                  className="catalog-add-product__image"
                  src={thumbnail.url}
                  alt={thumbnail?.name || product?.name || 'product'}
                />{' '}
              </div>
            ))}
          </ReactSortable>
          <div className="catalog-add-product__upload-icon">
            <AddImageIcon onClick={onUploadClick} />
          </div>
        </div>
        <div className="catalog-add-product__fields-section">
          <Form
            form={addProductForm}
            name="add-product-form"
            layout="vertical"
            initialValues={{
              _id: product?._id || '',
              discountType: product?.discount?.unit || '$',
              discount: product?.discount?.value || 0,
              name: product?.name || '',
              items: product?.price?.items || 1,
              price: product?.price?.amount || 0,
              paymentType: product?.price?.type || '',
              quantity: product?.price?.quantity || 1,
              description: product?.description || '',
              images:
                product?.images?.map((image) => ({
                  uid: image._id,
                  name: image.name,
                  url: image.src,
                })) || [],
            }}>
            <Form.Item
              label={<span className="catalog-add-product__label">Product name (Deliverable)</span>}
              name="name"
              rules={[
                { required: true, message: 'Please enter product name' },
                { whitespace: true, message: 'Field cannot be only spaces' },
              ]}>
              <Input className="catalog-add-product__input" placeholder="Enter product name" />
            </Form.Item>
            <Form.Item
              label={<span className="catalog-add-product__label">Select/create folder</span>}>
              <TreeSelect
                showSearch
                dropdownRender={(menu) => <div className="select-folder-tree-select">{menu}</div>}
                virtual={false}
                treeData={(treeData?.children || [])
                  .filter((item) => item.title !== null)
                  .map((item) => {
                    return {
                      ...item,
                      icon: item.create ? <AddFolder /> : <FolderIcon />,
                    };
                  })}
                defaultValue={
                  getFolderKeyFromTreeData(
                    product?.folderKey || selectedFolderKey,
                    treeData?.children || []
                  )?.key || null
                }
                treeIcon={true}
                placeholder="Select/create folder"
                suffixIcon={<DropdownIcon />}
                placement="bottom"
                onSelect={(value, info) => {
                  const selectedTree = value.split('_');

                  // If new folder is being created
                  if (value && typeof value === 'string' && value.match(/_new/)) {
                    const newValue = info.title;
                    addNewChildFromSearch(newValue);
                  } else {
                    onSelectTreeItem([selectedTree] || []);
                  }
                }}
                onSearch={(value) => {
                  setTreeSearch(value);
                  handleTreeSearch(value);
                }}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="catalog-add-product__label">
                  Quantity
                  <p className="catalog-add-product__label-information">
                    *Can change it later in the proposal
                  </p>
                </span>
              }
              name="items">
              <InputNumber
                min={1}
                className="catalog-add-product__input"
                placeholder="Enter quantity"
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="catalog-add-product__label">
                  Price
                  <p className="catalog-add-product__label-information">
                    *Value and currency are editable in the proposal
                  </p>
                </span>
              }
              name="price">
              <InputNumber
                className="catalog-add-product__input"
                placeholder="Enter price"
                onChange={() => {
                  // Trigger validation on discount field when price changes
                  addProductForm.validateFields(['discount']);
                }}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="catalog-add-product__label">
                  Payment type
                  <p className="catalog-add-product__label-information">
                    *Can change it later in the proposal
                  </p>
                </span>
              }
              name="paymentType">
              <EditableSelect
                items={['One-Time', 'Monthly', 'Annually', 'Hourly']}
                selectClassName="price-options-dropdown"
                inputClassName="price-options-input"
                placeholder="Payment Type"
                x={{ type: paymentType || '', id: 'paymentType' }}
                handleOptionsDropdown={(value, id) => {
                  if (addProductForm.getFieldValue('paymentType') !== value) {
                    setPaymentType(value);
                    addProductForm.setFieldsValue({
                      paymentType: value,
                    });
                  }
                }}
                configText={() => {}}
                dropdownClassName={
                  user.language === 'hebrew' || user.language === 'arabic'
                    ? 'wizard-language-rtl'
                    : ''
                }
                suffixIcon={<DropdownIcon />}
              />
            </Form.Item>
            {paymentType && ['Hourly', 'Monthly', 'Annually'].includes(paymentType) && (
              <Form.Item
                label={
                  <span className="catalog-add-product__label">
                    Duration
                    <p className="catalog-add-product__label-information">
                      *Can change it later in the proposal
                    </p>
                  </span>
                }
                name="quantity">
                <InputNumber
                  min={1}
                  className="catalog-add-product__input"
                  placeholder="Enter duration"
                />
              </Form.Item>
            )}
            <Form.Item
              label={
                <span className="catalog-add-product__label">
                  Discount
                  <p className="catalog-add-product__label-information">
                    *Can change it later in the proposal
                  </p>
                </span>
              }
              style={{ marginBottom: 0 }}>
              <div className="catalog-add-product__discount-row">
                <Form.Item
                  name="discount"
                  noStyle
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value) {
                          if (discountType === '%') {
                            // For percentage discount, check if it exceeds 100%
                            if (parseFloat(value) > 100) {
                              // Reset discount to 100%
                              addProductForm.setFieldsValue({
                                discount: 100,
                              });
                              return Promise.resolve();
                            }
                          } else {
                            // For fixed amount discount, check if it exceeds price
                            const price = addProductForm.getFieldValue('price') || 0;
                            if (parseFloat(value) > parseFloat(price) && parseFloat(price) > 0) {
                              // Reset discount to price value
                              addProductForm.setFieldsValue({
                                discount: price,
                              });
                              return Promise.resolve();
                            } else if (parseFloat(price) < 0) {
                              addProductForm.setFieldsValue({
                                discount: 0,
                              });
                              return Promise.resolve();
                            }
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <Input
                    className="catalog-add-product__input"
                    addonAfter={
                      <div className="catalog-add-product__discount-type">
                        <Select
                          value={discountType || currency}
                          onChange={(value) => {
                            setDiscountType(value);
                            addProductForm.setFieldsValue({
                              discountType: value,
                            });
                            // Trigger validation when discount type changes
                            addProductForm.validateFields(['discount']);
                          }}
                          suffixIcon={<DropdownIcon />}
                          placement="bottom"
                          dropdownClassName="is-legacy">
                          <Select.Option value={currency}>{currency}</Select.Option>
                          <Select.Option value="%">%</Select.Option>
                        </Select>
                      </div>
                    }
                    placeholder="Discount"
                    onChange={() => {
                      // Trigger validation when discount value changes
                      addProductForm.validateFields(['discount']);
                    }}
                  />
                </Form.Item>
              </div>
            </Form.Item>
          </Form>
        </div>
        <div className="catalog-add-product__description-section">
          <Form layout="vertical" form={addProductForm}>
            <Form.Item
              label={
                <span className="catalog-add-product__description-label">Add description</span>
              }
              name="description">
              <TextArea
                rows={12}
                className="catalog-add-product__description-textarea"
                placeholder="Add description"
              />
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

/**
 * Default props
 */
CatalogAddProduct.defaultProps = {
  product: {},
  treeData: {},
  onUploadClick: () => {},
  catalogThumbnails: [],
  user: {},
  selectedFolderKey: '',
  setTreeSearch: () => {},
  handleTreeSearch: () => {},
  addNewChildFromSearch: () => {},
  onSelectTreeItem: () => {},
};

/**
 * Props
 */
CatalogAddProduct.propTypes = {
  addProductForm: PropTypes.object.isRequired,
  product: PropTypes.object,
  treeData: PropTypes.object,
  onUploadClick: PropTypes.func,
  catalogThumbnails: PropTypes.array,
  user: PropTypes.object,
  setCatalogThumbnails: PropTypes.func,
  selectedFolderKey: PropTypes.string,
  setTreeSearch: PropTypes.func,
  handleTreeSearch: PropTypes.func,
  addNewChildFromSearch: PropTypes.func,
  onSelectTreeItem: PropTypes.func,
};

export default CatalogAddProduct;
